import React, { useState } from 'react';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, IconButton, useMediaQuery, useTheme, Typography, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import ChurchIcon from '@mui/icons-material/Church';
import AnnouncementIcon from '@mui/icons-material/Campaign';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PersonIcon from '@mui/icons-material/Person';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import GroupsIcon from '@mui/icons-material/Groups';

const drawerWidth = 240;

function Sidebar({ user, mobileOpen: externalMobileOpen, onDrawerToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [internalMobileOpen, setInternalMobileOpen] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const mobileOpen = externalMobileOpen !== undefined ? externalMobileOpen : internalMobileOpen;

  const handleDrawerToggle = () => {
    if (onDrawerToggle) {
      onDrawerToggle();
    } else {
      setInternalMobileOpen(!internalMobileOpen);
    }
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) {
      if (onDrawerToggle) {
        // If we have external control, we need to check current state
        // Since we're closing, we'll just call toggle if currently open
        if (mobileOpen) {
          onDrawerToggle();
        }
      } else {
        setInternalMobileOpen(false);
      }
    }
  };

  const superAdminMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/super-admin/dashboard' },
    { text: 'Churches', icon: <ChurchIcon />, path: '/super-admin/churches' },
    { text: 'All Users', icon: <PeopleIcon />, path: '/super-admin/users' },
    { text: 'Church Admins', icon: <PersonIcon />, path: '/super-admin/church-admins' },
    { text: 'Profile', icon: <PersonIcon />, path: '/super-admin/profile' },
  ];

  const churchAdminMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/church-admin/dashboard' },
    { text: 'Members', icon: <PeopleIcon />, path: '/church-admin/members' },
    { text: 'Announcements', icon: <AnnouncementIcon />, path: '/church-admin/announcements' },
    { text: 'Sermons', icon: <VideoLibraryIcon />, path: '/church-admin/sermons' },
    { text: 'Community', icon: <GroupsIcon />, path: '/church-admin/community' },
    { text: 'Prayer Requests', icon: <FavoriteIcon />, path: '/church-admin/prayer-requests' },
    { text: 'Attendance', icon: <PeopleIcon />, path: '/church-admin/attendance' },
    { text: 'Transactions', icon: <VolunteerActivismIcon />, path: '/church-admin/transactions' },
    { text: 'User Display', icon: <DisplaySettingsIcon />, path: '/church-admin/user-display' },
    { text: 'My Church', icon: <ChurchIcon />, path: '/church-admin/profile' },
    { text: 'My Profile', icon: <PersonIcon />, path: '/church-admin/my-profile' },
  ];

  const churchUserMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/church-user/dashboard' },
    { text: 'Announcements', icon: <AnnouncementIcon />, path: '/church-user/announcements' },
    { text: 'Sermons', icon: <VideoLibraryIcon />, path: '/church-user/sermons' },
    { text: 'Community', icon: <GroupsIcon />, path: '/church-user/community' },
    { text: 'Prayer Requests', icon: <FavoriteIcon />, path: '/church-user/prayer-requests' },
    { text: 'My Attendance', icon: <PeopleIcon />, path: '/church-user/attendance' },
    { text: 'Profile', icon: <PersonIcon />, path: '/church-user/profile' },
  ];

  const legacyAdminMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
  ];

  const legacyUserMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/user/dashboard' },
    { text: 'Profile', icon: <PeopleIcon />, path: '/user/profile' },
  ];

  let menuItems = [];
  if (user?.role === 'super_admin') {
    menuItems = superAdminMenuItems;
  } else if (user?.role === 'church_admin') {
    menuItems = churchAdminMenuItems;
  } else if (user?.role === 'church_user') {
    menuItems = churchUserMenuItems;
  } else if (user?.role === 'admin') {
    menuItems = legacyAdminMenuItems;
  } else {
    menuItems = legacyUserMenuItems;
  }

  const drawerContent = (
    <>
      <Toolbar>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            py: 2,
          }}
        >
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontFamily: 'sans-serif',
              fontWeight: 700,
              color: '#d4af39', // Gold/Bronze from logo
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1), 0 0 1px rgba(255, 255, 255, 0.8)',
              letterSpacing: '0.1em',
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
            }}
          >
            HOSANNA
          </Typography>
          {isMobile && (
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                color: '#d4af39',
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavClick(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}

export default Sidebar;

